if (!customElements.get('sa-choose-your-plaud')) {
  class SaChooseYourPlaud extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.syncHeights = this.syncHeights.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      this.addEventListener('keydown', this.handleKeydown);
      this.syncHeights();

      if (!this._resizeHandler) {
        this._resizeHandler = () => {
          window.clearTimeout(this._resizeTimer);
          this._resizeTimer = window.setTimeout(this.syncHeights, 50);
        };
        window.addEventListener('resize', this._resizeHandler);
      }

      if (!this._ro && typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(() => this.syncHeights());
        this._ro.observe(this);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('keydown', this.handleKeydown);
      if (this._resizeHandler) {
        window.removeEventListener('resize', this._resizeHandler);
        this._resizeHandler = null;
      }
      if (this._resizeTimer) {
        window.clearTimeout(this._resizeTimer);
        this._resizeTimer = null;
      }
      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }
    }

    handleClick(event) {
      const swatch = event.target.closest('.sa-choose-your-plaud__swatch');
      if (!swatch || !this.contains(swatch)) return;

      this.selectSwatch(swatch);
    }

    handleKeydown(event) {
      const button = event.target.closest('select-option[role="button"]');
      if (!button || !this.contains(button)) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;

      event.preventDefault();
      button.click();
    }

    selectSwatch(swatch) {
      const card = swatch.closest('.sa-choose-your-plaud__card');
      const swatches = swatch.closest('.sa-choose-your-plaud__swatches');
      if (!card || !swatches) return;

      const position = Number.parseInt(swatches.dataset.position || '', 10);
      const value = swatch.dataset.value || '';
      const activeVariant = card.querySelector('.sa-choose-your-plaud__variant-data.is-active')
        || this.getInitialVariant(card);
      if (!position || !value || !activeVariant) return;

      const optionValues = (activeVariant.dataset.title || '').split(' / ');
      optionValues[position - 1] = value;
      const targetTitle = optionValues.join(' / ');
      const variants = Array.from(card.querySelectorAll('.sa-choose-your-plaud__variant-data'));
      const targetVariant = variants.find((variant) => variant.dataset.title === targetTitle)
        || variants.find((variant) => {
          const parts = (variant.dataset.title || '').split(' / ');
          return parts[position - 1] === value;
        });

      if (!targetVariant) return;

      variants.forEach((variant) => variant.classList.toggle('is-active', variant === targetVariant));
      swatches.querySelectorAll('.sa-choose-your-plaud__swatch').forEach((item) => {
        const isActive = item === swatch;
        item.classList.toggle('is-active', isActive);
        item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.updatePrice(card, targetVariant);
      this.updateDiscount(card, targetVariant);
      this.updateLinks(card, targetVariant);
      this.updateMedia(card, targetVariant);
    }

    getInitialVariant(card) {
      const activeSwatch = card.querySelector('.sa-choose-your-plaud__swatch.is-active');
      const swatches = activeSwatch?.closest('.sa-choose-your-plaud__swatches');
      const position = Number.parseInt(swatches?.dataset.position || '', 10);
      const value = activeSwatch?.dataset.value || '';
      const variants = Array.from(card.querySelectorAll('.sa-choose-your-plaud__variant-data'));
      const initialVariant = variants.find((variant) => {
        const parts = (variant.dataset.title || '').split(' / ');
        return parts[position - 1] === value && variant.dataset.available === 'true';
      }) || variants.find((variant) => variant.dataset.available === 'true') || variants[0];

      if (initialVariant) initialVariant.classList.add('is-active');
      return initialVariant;
    }

    updatePrice(card, variant) {
      const salePriceDesktop = card.querySelector('[data-sale-price-desktop]');
      const salePriceMobile = card.querySelector('[data-sale-price-mobile]');
      const comparePrice = card.querySelector('.sa-choose-your-plaud__compare-price');
      const comparePriceDesktop = card.querySelector('[data-compare-price-desktop]');
      const comparePriceMobile = card.querySelector('[data-compare-price-mobile]');

      if (salePriceDesktop && variant.dataset.price) salePriceDesktop.textContent = variant.dataset.price;
      if (salePriceMobile && variant.dataset.priceMobile) salePriceMobile.textContent = variant.dataset.priceMobile;
      if (!comparePrice) return;

      const comparePriceText = variant.dataset.comparePrice || '';
      const comparePriceMobileText = variant.dataset.comparePriceMobile || '';
      if (comparePriceDesktop) comparePriceDesktop.textContent = comparePriceText;
      if (comparePriceMobile) comparePriceMobile.textContent = comparePriceMobileText;
      comparePrice.hidden = !comparePriceText;
    }

    updateDiscount(card, variant) {
      const discount = card.querySelector('.sa-choose-your-plaud__discount');
      if (!discount) return;

      const discountText = variant.dataset.discountInfo || '';
      discount.textContent = discountText;
      discount.hidden = !discountText;
    }

    updateLinks(card, variant) {
      const variantId = variant.dataset.id || '';
      const mediaLink = card.querySelector('.sa-choose-your-plaud__media');
      const titleLink = card.querySelector('.sa-choose-your-plaud__title');
      const quickView = card.querySelector('select-option');
      const baseProductUrl = (titleLink?.href || mediaLink?.href || '').split('?')[0];
      if (!baseProductUrl || !variantId) return;

      const productUrl = `${baseProductUrl}?variant=${variantId}`;
      if (mediaLink) mediaLink.href = productUrl;
      if (titleLink) titleLink.href = productUrl;

      if (quickView) {
        const quickViewUrl = `${productUrl}&section_id=product-quickview&ajax=1`;
        quickView.dataset.url = quickViewUrl;
        quickView.url = quickViewUrl;
      }
    }

    updateMedia(card, variant) {
      if (card.dataset.fixedMedia === 'true') return;

      const image = card.querySelector('[data-product-image]');
      const source = card.querySelector('[data-product-source]');
      const imageSrc = variant.dataset.imageSrc || '';
      if (!image || !imageSrc) return;

      image.src = imageSrc;
      if (source) source.srcset = imageSrc;
      if (variant.dataset.imageWidth) image.width = Number.parseInt(variant.dataset.imageWidth, 10);
      if (variant.dataset.imageHeight) image.height = Number.parseInt(variant.dataset.imageHeight, 10);
    }

    syncHeights() {
      const cards = Array.from(this.querySelectorAll('.sa-choose-your-plaud__card'));
      if (!cards.length) return;

      const titles = cards.map((card) => card.querySelector('.sa-choose-your-plaud__title')).filter(Boolean);
      const descriptions = cards
        .map((card) =>
          Array.from(card.querySelectorAll('.sa-choose-your-plaud__description')).find(
            (description) => window.getComputedStyle(description).display !== 'none',
          ),
        )
        .filter(Boolean);

      this.style.setProperty('--sa-cyp-title-track', 'auto');
      this.style.setProperty('--sa-cyp-description-track', 'auto');

      const maxTitle = titles.reduce((max, el) => Math.max(max, el.getBoundingClientRect().height), 0);
      const maxDescription = descriptions.reduce((max, el) => Math.max(max, el.getBoundingClientRect().height), 0);

      if (maxTitle > 0) {
        this.style.setProperty('--sa-cyp-title-track', `${Math.ceil(maxTitle)}px`);
      }
      if (maxDescription > 0) {
        this.style.setProperty('--sa-cyp-description-track', `${Math.ceil(maxDescription)}px`);
      }
    }
  }

  customElements.define('sa-choose-your-plaud', SaChooseYourPlaud);
}
